import i18n from '@/utils/i18n';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Analytics } from '@/utils/analytics';
import { ApolloError, MutationTuple, useApolloClient } from '@apollo/client';
import {
  CartInputsHookParams,
  CartInputsHookValue,
  FulfillmentHookParams,
  FulfillmentHookValue,
  QuantityOverrides,
  SelectedFulfillmentType,
  UpdateCartOptions,
  UpdateQuantityCallbackType,
  UpdateQuantityMetadata,
  UpdateQuantityResponse,
  VariantPackDetails,
} from './new-order-actions-context-types';
import {
  createOrderItemsInput,
  OrderQuantityActions,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import {
  getCartKeyForOrder,
  InputValueMap,
  isDefaultCart,
  mergeItems,
} from '@/utils/new-order';
import {
  getCodeFromGQLError,
  getMessageFromGQLError,
} from '@/utils/apollo-client';
import {
  ItemsContext_MultiOrderGuideItemsQuery,
  ItemsContext_VariantsPackFieldsFragment,
  NewOrder_CreateOrderMutation,
  NewOrder_CreateOrderMutationVariables,
  NewOrder_DeleteOrderMutation,
  NewOrder_DeleteOrderMutationVariables,
  NewOrder_OrderContextQuery,
  NewOrder_OrderFieldsFragment,
  NewOrder_PunchoutOrderMessageFieldsFragment,
  NewOrder_UpdateOrderMetadataMutation,
  NewOrder_UpdateOrderMetadataMutationVariables,
  NewOrder_VisibleVariantPackQuery,
  OrderDomain,
  Orders_Set_Input,
  OrderStatus,
  SubmitOrderLoyaltyEarn,
  UpdateItemInput,
} from '@/types';
import { NewOrderLoadingContext } from '@/contexts/new-order-loading-context';
import { OrderSummaryInputID, OrderValidationErrorCode } from '@/enums';
import { PaymentMethodOption } from './payments-context';
import {
  removeCachedOrder,
  useNewOrderActionsQueries,
} from '@/hooks/use-new-order-actions-queries';
import { toAdyenPaymentJson } from '@/utils/payments';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useCartInputs } from '@/hooks/use-cart-inputs';
import { useDebouncedCallback } from 'use-debounce';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useLocalScanData } from '@/hooks/offline-mode/use-local-scan-data';
import { useNewOrderContext } from './new-order-context';
import { useNewOrderQuantityStoreApi } from '@/stores/new-order-quantity-store';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import {
  useOfflineOrderContext,
  useOfflineOrderGuide,
  useOfflineVisibleVariantPacks,
} from '@/hooks/use-offline-data';
import { useOrderAlreadyPlacedToast } from '@/hooks/use-order-already-placed-toast';
import { usePrevious } from '@/hooks/use-previous';
import { usePunchoutParams } from '@/hooks/use-punchout-params';
import { useSelectedFulfillment } from '@/hooks/use-selected-fulfillment';
import { useToasts } from '@/contexts/toast-context';
import { v4 as uuidv4 } from 'uuid';
import { VariantPackEntry } from '@/adapters/offline-mode';

interface OfflineVariantPackItemMap {
  [variantPackUUID: string]: ItemsContext_VariantsPackFieldsFragment['item'];
}

const UPDATE_QUANTITY_DEBOUNCE_DELAY_MS = 5000;
export const OFFLINE_UPDATE_QUANTITY_DEBOUNCE_DELAY_MS = 1000;

const localScanToOfflineVariantPackItem = (
  localScanData: VariantPackEntry,
): ItemsContext_VariantsPackFieldsFragment['item'] => {
  return {
    description: localScanData.description,
    display_name: localScanData.display_name,
    restaurant_item_overrides: [],
    uuid: localScanData.item_uuid,
  };
};

/**
 * Fallback for an item added offline that isn't in any downloaded catalog so
 * we only have the display name to work with
 */
const variantPackDetailsToOfflineVariantPackItem = (
  variantPackDetails: VariantPackDetails | undefined,
): ItemsContext_VariantsPackFieldsFragment['item'] | undefined => {
  if (!variantPackDetails) {
    return undefined;
  }

  return {
    description: null,
    display_name: variantPackDetails.itemDisplayName,
    restaurant_item_overrides: [],
    // Set to empty instead of the variant pack, otherwise it would appear
    // valid but reference the wrong record
    uuid: '',
  };
};

/** Offline drafts go in the default cart, so the offline cache's GLOBAL-only filter keeps them. */
export const buildOfflineDraftOrder = (
  orderUUID: string,
  fulfillment: SelectedFulfillmentType | null,
  ordersItems: NewOrder_OrderFieldsFragment['orders_items'],
) => ({
  fulfillment_type: fulfillment?.type.toUpperCase() || '',
  order_domain: OrderDomain.Global,
  order_fulfillment_fees: [],
  order_invoices: [],
  orders_items: ordersItems,
  restaurant_desired_delivery_time: fulfillment?.date,
  status: 'DRAFT',
  uuid: orderUUID,
});

/**
 * Visible variant packs overwrite order guide items since they have complete
 * variant pack data. Local scan entries only fill gaps otherwise
 */
export const buildOfflineVariantPackItemMap = ({
  localScanEntriesByUUID,
  orderGuideItems,
  visibleVariantPacks,
}: {
  localScanEntriesByUUID?: Map<string, VariantPackEntry>;
  orderGuideItems?: ItemsContext_MultiOrderGuideItemsQuery['getOrderGuideItems'];
  visibleVariantPacks?: NewOrder_VisibleVariantPackQuery['visible_variant_packs'];
}): OfflineVariantPackItemMap => {
  const orderGuideMap = (
    orderGuideItems || []
  ).reduce<OfflineVariantPackItemMap>((acc, orderGuideItem) => {
    const variantPack = orderGuideItem.variant_pack;
    if (variantPack) {
      acc[variantPack.uuid] = variantPack.item;
    }
    return acc;
  }, {});

  // OfflineVisibleVariantPacks has full VP data, so use it if available
  const vpItemMap = (
    visibleVariantPacks || []
  ).reduce<OfflineVariantPackItemMap>((acc, visibleVariantPack) => {
    const variantPack = visibleVariantPack.variant_pack;
    if (variantPack) {
      acc[variantPack.uuid] = variantPack.item;
    }
    return acc;
  }, orderGuideMap);

  // Doesn't do anything on mount (before fetchLocalScanEntries is called)
  // Will properly include local scan data after first updateCartCallback call
  for (const [vpUUID, entry] of localScanEntriesByUUID || []) {
    if (!vpItemMap[vpUUID]) {
      vpItemMap[vpUUID] = localScanToOfflineVariantPackItem(entry);
    }
  }

  return vpItemMap;
};

export const getUpdatedItems = (
  state: QuantityStateType,
  source: string,
  overrides: QuantityOverrides = {},
  options: UpdateCartOptions = {},
): UpdateItemInput[] => {
  const updatedItemMap = options.ignoreLocalQuantityChanges
    ? {}
    : createOrderItemsInput(state);
  const itemMap = {
    ...updatedItemMap,
    ...overrides,
  };
  return Object.keys(itemMap).map((variantPackUUID) => {
    const eventPayloads = state.eventPayloadMap[variantPackUUID];
    const variantPackDetailMap = state.variantPackDetailMap[variantPackUUID];

    return {
      cart_details_at_time_of_order: {
        quantity: itemMap[variantPackUUID],
      },
      variant_pack_id: variantPackUUID,
      ...(variantPackDetailMap
        ? {
            item_name: variantPackDetailMap.itemDisplayName,
            // Pre-populate order item notes from order guide request field
            ...(variantPackDetailMap.orderGuideNotes
              ? { item_notes: variantPackDetailMap.orderGuideNotes }
              : {}),
          }
        : {}),
      ...(options.itemNotes?.[variantPackUUID]
        ? { item_notes: options.itemNotes[variantPackUUID] }
        : {}),
      ...(eventPayloads
        ? {
            add_to_cart_event_payloads:
              eventPayloads.addToCartEventPayloads.map((payload) => {
                payload.source = source;
                return payload;
              }),
            purchase_event_payloads: eventPayloads.purchaseEventPayloads,
          }
        : {}),
    };
  });
};

// The order from an update that reached storage, including a partial save
export const getUpdatedOrder = (
  result: UpdateQuantityResponse,
): NewOrder_OrderFieldsFragment | null =>
  result.status === 'saved' || result.status === 'partial'
    ? result.order
    : null;

// Nothing needed saving, or the update left an order to show
export const shouldShowOrder = (result: UpdateQuantityResponse): boolean =>
  result.status === 'no_changes' || Boolean(getUpdatedOrder(result));

// Some or all quantities never reached storage, so they are still pending
export const hasUnsavedQuantities = (result: UpdateQuantityResponse): boolean =>
  result.status === 'failed' || result.status === 'partial';

export interface NewOrderActionsContextType {
  additionalInputValueMap: InputValueMap;
  cancelPendingInputs: () => void;
  clearCachedOrder: () => Promise<void>;
  clearCart: () => Promise<{ success: boolean }>;
  createOrder: MutationTuple<
    NewOrder_CreateOrderMutation,
    NewOrder_CreateOrderMutationVariables
  >[0];
  debouncedUpdateQuantity: UpdateQuantityCallbackType;
  deleteOrder: MutationTuple<
    NewOrder_DeleteOrderMutation,
    NewOrder_DeleteOrderMutationVariables
  >[0];
  fulfillmentHydrated: boolean;
  /** Where a new cart's fulfillment starts, so it is not mistaken for reusing the active cart's settings. */
  nextAvailableFulfillment: SelectedFulfillmentType | null;
  replaceOrderQuantities: (
    desiredQuantities: QuantityOverrides,
    source: string,
    options?: Pick<UpdateCartOptions, 'itemNotes'>,
  ) => Promise<UpdateQuantityResponse>;
  selectedFulfillment: SelectedFulfillmentType | null;
  selectFulfillment: (fulfillment: SelectedFulfillmentType | null) => void;
  setAdditionalInputValue: (
    inputID: string,
    inputTitle: string,
    inputValue: string,
  ) => void;
  setNextFulfillment: (
    newFulfillment: SelectedFulfillmentType,
  ) => Promise<void>;
  setOrderPaymentMethod: (newPaymentMethod: PaymentMethodOption | null) => void;
  submitOrder: (smartOrderUUID: string | null) => Promise<{
    loyaltyEarns: SubmitOrderLoyaltyEarn[];
    order: NewOrder_OrderFieldsFragment | null;
    punchoutOrderMessage?: NewOrder_PunchoutOrderMessageFieldsFragment | null;
    splitOrderUUIDs: string[];
  }>;
  updateAndClearQuantity: (
    source: string,
    pendingQuantities?: QuantityOverrides,
  ) => Promise<UpdateQuantityResponse>;
  updateAndSetQuantity: (
    source: string,
    pendingQuantities?: QuantityOverrides,
  ) => Promise<UpdateQuantityResponse>;
  updateFulfillment: (setInput: Orders_Set_Input) => Promise<void>;
  updateOrderItemNotes: (
    notes: string | null,
    variantPackUUID: string,
    options?: {
      onSuccess?: (order: NewOrder_OrderFieldsFragment) => void;
    },
  ) => Promise<void>;
  updateOrderMetadata: MutationTuple<
    NewOrder_UpdateOrderMetadataMutation,
    NewOrder_UpdateOrderMetadataMutationVariables
  >[0];
}

export const NewOrderActionsContext =
  createContext<NewOrderActionsContextType | null>(null);

export const useNewOrderActionsContext = () => {
  const context = useContext(NewOrderActionsContext);
  if (!context) {
    throw new Error(
      'useNewOrderActionsContext requires a NewOrderActionsContextProvider',
    );
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
  orderDomain?: OrderDomain;
  useCartInputsHook?: (params: CartInputsHookParams) => CartInputsHookValue;
  useFulfillmentHook?: (params: FulfillmentHookParams) => FulfillmentHookValue;
}

export const NewOrderActionsContextProvider: React.FC<Props> = ({
  children,
  orderDomain = OrderDomain.Global,
  useCartInputsHook = useCartInputs,
  useFulfillmentHook = useSelectedFulfillment,
}) => {
  const apolloClient = useApolloClient();
  const isOfflineModeActive = useOfflineModeActive();
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const quantityStore = useNewOrderQuantityStoreApi();
  const {
    dispatchQuantityReducerAction,
    dispatchQuantityUpdateAction,
    draftOrder,
    multiCartEnabled,
    orderDomains,
    restaurantUUID,
    supplierUUID,
    refetchOfflineDraftOrder,
    setDraftOrderAndHydrateQuantities,
  } = useNewOrderContext();
  const {
    clearCart,
    createOrder,
    deleteOrder,
    isUpdatingCart,
    isUpdatingFulfillment,
    submitOrder,
    updateCachedOrderContext,
    updateCart,
    updateFulfillment,
    updateOrderAdditionalInputs,
    updateOrderMetadata,
    updateOrderNotes,
    updateOrderPoNumber,
  } = useNewOrderActionsQueries(restaurantUUID!, supplierUUID!, orderDomains);

  const activeDraftOrderUUIDRef = useRef(draftOrder?.uuid);
  useEffect(() => {
    activeDraftOrderUUIDRef.current = draftOrder?.uuid;
  }, [draftOrder?.uuid]);
  const { offlineOrderContext, saveOfflineOrderContext } =
    useOfflineOrderContext(restaurantUUID!, supplierUUID!);

  const resetToDefaultFulfillment = useDynamicConfigValue(
    'reset_fulfillment_on_order_submission',
  );

  const offlineOrderGuide = useOfflineOrderGuide(
    restaurantUUID!,
    supplierUUID!,
  );
  const offlineVisibleVariantPacks = useOfflineVisibleVariantPacks(
    restaurantUUID!,
    supplierUUID!,
  );
  const {
    entriesByUUID: localScanEntriesByUUID,
    fetchEntries: fetchLocalScanEntries,
    hasData: localScanHasData,
  } = useLocalScanData();

  const punchoutParams = usePunchoutParams();
  const showOrderAlreadyPlacedToast = useOrderAlreadyPlacedToast();
  const { showErrorToast } = useToasts();

  const {
    additionalInputValueMap,
    cancelPendingInputs,
    clearActiveCartInputs,
    flushPendingInputs,
    setAdditionalInputValue: setAdditionalInputValueCallback,
  } = useCartInputsHook({
    updateOrderAdditionalInputs,
    updateOrderNotes,
    updateOrderPoNumber,
  });
  const [orderPaymentMethod, setOrderPaymentMethod] =
    useState<PaymentMethodOption | null>(null);

  const localScanEntriesFetched = useRef(false);

  const offlineVariantPackItemMap = useMemo(() => {
    if (!isOfflineModeActive) {
      return {};
    }

    return buildOfflineVariantPackItemMap({
      localScanEntriesByUUID,
      orderGuideItems: offlineOrderGuide.data?.getOrderGuideItems,
      visibleVariantPacks:
        offlineVisibleVariantPacks.data?.visible_variant_packs,
    });
  }, [
    isOfflineModeActive,
    localScanEntriesByUUID,
    offlineOrderGuide.data?.getOrderGuideItems,
    offlineVisibleVariantPacks.data?.visible_variant_packs,
  ]);

  const clearCachedOrder = useCallback(async () => {
    if (draftOrder?.uuid) {
      await updateCachedOrderContext(
        apolloClient,
        removeCachedOrder(draftOrder.uuid),
      );
    }
  }, [apolloClient, draftOrder?.uuid, updateCachedOrderContext]);

  const updateFulfillmentCallback = useCallback(
    async (setInput: Orders_Set_Input) => {
      if (!draftOrder) {
        return;
      }

      try {
        const result = await updateFulfillment({
          variables: {
            orderUUID: draftOrder.uuid,
            set: setInput,
            unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
          },
        });
        if (result.data?.update_orders?.returning?.length !== 1) {
          await clearCachedOrder();
        }
      } catch (e) {
        console.error('Error updating fulfillment', e);
        throw e;
      }
    },
    [clearCachedOrder, draftOrder, updateFulfillment],
  );

  const {
    fulfillmentHydrated,
    nextAvailableFulfillment,
    resetFulfillmentToNextAvailable,
    selectFulfillment,
    selectedFulfillment,
  } = useFulfillmentHook({
    updateFulfillment: updateFulfillmentCallback,
  });

  const submitOrderCallback = useCallback(
    async (smartOrderUUID: string | null) => {
      if (!draftOrder) {
        return {
          loyaltyEarns: [],
          order: null,
          punchoutOrderMessage: null,
          splitOrderUUIDs: [],
        };
      }

      const additionalInputValues = [];
      let orderNotes: string | null = null;
      let poNumber: string | null = null;

      for (const [inputID, { title, value }] of Object.entries(
        additionalInputValueMap,
      )) {
        const inputValue = value.trim();
        switch (inputID) {
          case OrderSummaryInputID.Notes:
            orderNotes = inputValue;
            break;
          case OrderSummaryInputID.PONumber:
            poNumber = inputValue;
            break;
          default:
            additionalInputValues.push({
              id: inputID,
              title,
              value: inputValue,
            });
            break;
        }
      }

      const paymentMethodJson = orderPaymentMethod
        ? toAdyenPaymentJson(orderPaymentMethod)
        : null;

      cancelPendingInputs();

      const result = await submitOrder({
        variables: {
          additionalInputValues,
          orderNotes,
          orderUUID: draftOrder.uuid,
          paymentMethod: paymentMethodJson,
          poNumber,
          punchoutCookie: punchoutParams?.cookie,
          smartOrderUUID: smartOrderUUID || undefined,
        },
      });

      const submittedOrder = result.data?.submitOrder.order;
      if (submittedOrder?.status !== OrderStatus.InReview) {
        clearActiveCartInputs();
        // some distributors require resetting the fulfillment date to the next
        // available date when the order is submitted.
        if (resetToDefaultFulfillment) {
          resetFulfillmentToNextAvailable();
        }
      }

      return {
        loyaltyEarns: result.data?.submitOrder.loyalty_earns ?? [],
        order: submittedOrder ?? null,
        punchoutOrderMessage: result.data?.submitOrder.punchout_order_message,
        splitOrderUUIDs: result.data?.submitOrder.split_order_id_list ?? [],
      };
    },
    [
      additionalInputValueMap,
      cancelPendingInputs,
      draftOrder,
      orderPaymentMethod,
      punchoutParams?.cookie,
      clearActiveCartInputs,
      submitOrder,
      resetToDefaultFulfillment,
      resetFulfillmentToNextAvailable,
    ],
  );

  const updateCartCallback = useCallback(
    async (
      overrides: QuantityOverrides = {},
      metadata: UpdateQuantityMetadata,
      options?: UpdateCartOptions,
    ): Promise<UpdateQuantityResponse> => {
      const quantityState = quantityStore.getState().quantityState;
      if (
        multiCartEnabled &&
        draftOrder?.uuid &&
        metadata.orderUUID !== draftOrder.uuid
      ) {
        return { status: 'no_changes' };
      }
      const updatedItems = getUpdatedItems(
        quantityState,
        metadata.source,
        overrides,
        options,
      );
      if (!updatedItems.length) {
        return { status: 'no_changes' };
      }

      const isOfflineModeActiveWithOrder = Boolean(
        isOfflineModeActive && offlineOrderContext.data,
      );
      let updateResult = null;

      try {
        let orderUUID = draftOrder?.uuid;
        let manuallyUpdateDraft = false;
        let result;

        if (isOfflineModeActiveWithOrder) {
          // This makes sure local scan data isn't loaded until absolutely necessary
          // - manually push data into offlineVariantPackItemMap on first load
          // - offlineVariantPackItemMap will include local scan data via useMemo above on subsequent calls
          let vpItemMap = offlineVariantPackItemMap;
          if (localScanHasData && !localScanEntriesFetched.current) {
            localScanEntriesFetched.current = true;
            const { entriesByUUID } = await fetchLocalScanEntries();
            vpItemMap = { ...vpItemMap };
            for (const [vpUUID, entry] of entriesByUUID) {
              if (!vpItemMap[vpUUID]) {
                vpItemMap[vpUUID] = localScanToOfflineVariantPackItem(entry);
              }
            }
          }

          const ordersItems: NewOrder_OrderFieldsFragment['orders_items'] = [];
          const unresolvedVariantPackUUIDs: string[] = [];

          // Index the cart once instead of rescanning it for every updated item
          const draftOrderItemsByVPUUID = new Map(
            (draftOrder?.orders_items || []).map((orderItem) => [
              orderItem.variants_pack.uuid,
              orderItem,
            ]),
          );

          for (const item of updatedItems) {
            const variantPackUUID = item.variant_pack_id;
            const quantity = item.cart_details_at_time_of_order?.quantity;
            const existingOrderItem =
              draftOrderItemsByVPUUID.get(variantPackUUID);

            if (existingOrderItem) {
              ordersItems.push({
                ...existingOrderItem,
                order_item_prices: {
                  ...existingOrderItem.order_item_prices,
                  pack_quantity_at_order: quantity,
                },
              });
              continue;
            }

            // Removals are filtered out of the draft order below
            if (!quantity) {
              continue;
            }

            const offlineItemData =
              vpItemMap[variantPackUUID] ??
              variantPackDetailsToOfflineVariantPackItem(
                quantityState.variantPackDetailMap[variantPackUUID],
              );

            // Skip rather than fail the whole batch, so the rest still saves
            if (!offlineItemData) {
              unresolvedVariantPackUUIDs.push(variantPackUUID);
              continue;
            }

            ordersItems.push({
              earliest_fulfillment_at: null,
              order_item_prices: {
                pack_quantity_at_order: quantity,
              },
              restaurant_display_name:
                offlineItemData.restaurant_item_overrides[0]?.display_name ??
                offlineItemData.display_name,
              uuid: uuidv4(), // This is a workaround since a non-offline draft order expects a uuid.
              variants_pack: {
                item: {
                  description: offlineItemData.description,
                  uuid: offlineItemData.uuid,
                },
                metadata:
                  quantityState.variantPackDetailMap[variantPackUUID]
                    ?.metadata || null,
                uuid: variantPackUUID,
              },
            });
          }

          // Merge the offline draft order with the updated items.
          // If they have the same variant pack uuid, the updated item will be used.

          const mergedOrderItems = mergeItems([
            ...(draftOrder?.orders_items || []),
            ...ordersItems,
          ]);

          // need to filter out order items with quantity 0
          const filteredOrderItems = mergedOrderItems.filter(
            (item) =>
              item.order_item_prices &&
              item.order_item_prices.pack_quantity_at_order !== 0,
          );

          orderUUID = orderUUID || uuidv4();

          const newDraftOrder = buildOfflineDraftOrder(
            orderUUID,
            selectedFulfillment,
            filteredOrderItems,
          );

          // Delete the order if there are no items in the cart. Otherwise, create or update the offline draft order.
          const ordersToSave =
            filteredOrderItems.length !== 0 ? [newDraftOrder] : [];

          const newContextToSave: NewOrder_OrderContextQuery = {
            ...offlineOrderContext.data!,
            getOrders: ordersToSave.map((order) => ({
              order,
            })),
          };

          // Save the draft order into our offline storage, and wait for that operation
          // to complete before refetching (which populates the React state)
          await saveOfflineOrderContext(newContextToSave);

          // Refetch the offline draft order to trigger hydration.
          await refetchOfflineDraftOrder();
          updateResult = newDraftOrder;

          if (unresolvedVariantPackUUIDs.length) {
            Analytics.track('update_cart_failed', {
              isOfflineModeActiveWithOrder,
              localQuantityMap: quantityState.localQuantityMap,
              orderUUID,
              overrides,
              reason: 'offline_missing_variant_pack_data',
              savedQuantityMap: quantityState.savedQuantityMap,
              source: metadata.source,
              unresolvedVariantPackUUIDs,
            });

            return { order: updateResult, status: 'partial' };
          }
        } else {
          if (!orderUUID) {
            result = await createOrder({
              variables: {
                deliveryDate: selectedFulfillment?.date,
                fulfillmentType: selectedFulfillment?.type,
                orderDomain,
                restaurantUUID: restaurantUUID!,
                supplierUUID: supplierUUID!,
                timeslotEnd: selectedFulfillment?.timeslot?.endTime,
                timeslotStart: selectedFulfillment?.timeslot?.startTime,
              },
            });
            orderUUID = result.data!.createOrder.order!.uuid;
            manuallyUpdateDraft = true;
          }

          const updateCartResponse = await updateCart({
            variables: {
              orderUUID,
              updatedItems,
            },
          });

          updateResult = updateCartResponse.data?.updateCart.order || null;

          // Keep offline draft order in sync with changes from online, that way if the user
          // goes from offline -> online -> offline, they have an up to date state at the end.
          // Only do this if the chat is enabled for Offline Mode, as to not store unnecessary data.
          if (isOfflineModeEnabled) {
            await refetchOfflineDraftOrder();
          }
        }

        const isStaleCartResult =
          multiCartEnabled &&
          updateResult !== null &&
          activeDraftOrderUUIDRef.current !== undefined &&
          updateResult.uuid !== activeDraftOrderUUIDRef.current;

        if (manuallyUpdateDraft && !isStaleCartResult) {
          setDraftOrderAndHydrateQuantities(updateResult);
        }

        return { order: updateResult, status: 'saved' };

        // Get a map of existing variant pack uuids to items from the offline order guide.
      } catch (e) {
        // Check if the order was already placed, otherwise continue and let
        // the caller determine the appropriate error handling
        Analytics.track('update_cart_failed', {
          error:
            e instanceof ApolloError
              ? getMessageFromGQLError(e)
              : 'Unknown error',
          isOfflineModeActiveWithOrder,
          localQuantityMap: quantityState.localQuantityMap,
          orderUUID: draftOrder?.uuid,
          overrides,
          reason: 'error',
          savedQuantityMap: quantityState.savedQuantityMap,
          source: metadata.source,
        });

        if (isOfflineModeActiveWithOrder) {
          // Suppress the toast since the user can't act on it while offline anyway
          return { errorToastShown: false, status: 'failed' };
        }

        if (
          getCodeFromGQLError(e) ===
          OrderValidationErrorCode.ORDER_ALREADY_PLACED
        ) {
          showOrderAlreadyPlacedToast({ source: 'update_cart' });
          await clearCachedOrder();
          // 'saved' so callers clear local quantities - the cart is gone, not
          // broken, so there is nothing to retry
          return { order: null, status: 'saved' };
        }

        showErrorToast(i18n.t('toast.update_cart_failed'));
        return { errorToastShown: true, status: 'failed' };
      }
    },
    [
      draftOrder?.uuid,
      draftOrder?.orders_items,
      fetchLocalScanEntries,
      localScanHasData,
      multiCartEnabled,
      offlineVariantPackItemMap,
      offlineOrderContext.data,
      selectedFulfillment,
      saveOfflineOrderContext,
      refetchOfflineDraftOrder,
      updateCart,
      orderDomain,
      isOfflineModeActive,
      isOfflineModeEnabled,
      createOrder,
      restaurantUUID,
      supplierUUID,
      showErrorToast,
      showOrderAlreadyPlacedToast,
      clearCachedOrder,
      setDraftOrderAndHydrateQuantities,
      quantityStore,
    ],
  );

  const debouncedUpdateQuantity = useDebouncedCallback(
    updateCartCallback,
    isOfflineModeActive
      ? OFFLINE_UPDATE_QUANTITY_DEBOUNCE_DELAY_MS
      : UPDATE_QUANTITY_DEBOUNCE_DELAY_MS,
  );

  const activeCartKey = multiCartEnabled
    ? getCartKeyForOrder(draftOrder)
    : null;
  const previousCartKey = usePrevious(activeCartKey);
  useEffect(() => {
    if (!previousCartKey || previousCartKey === activeCartKey) {
      return;
    }
    debouncedUpdateQuantity.cancel();
    flushPendingInputs();
  }, [
    activeCartKey,
    debouncedUpdateQuantity,
    flushPendingInputs,
    previousCartKey,
  ]);

  const debouncedUpdateQuantityCallback: UpdateQuantityCallbackType =
    useCallback(
      (updateQuantityData, updateQuantityMetadata) => {
        const { newQuantity, variantPackUUID } = updateQuantityData;
        const metadataWithOrderUUID: UpdateQuantityMetadata = {
          ...updateQuantityMetadata,
          orderUUID: draftOrder?.uuid,
        };

        dispatchQuantityUpdateAction({
          payload: {
            ...updateQuantityData,
            ...metadataWithOrderUUID,
          },
        });

        debouncedUpdateQuantity(
          { [variantPackUUID]: newQuantity },
          metadataWithOrderUUID,
        );
      },
      [debouncedUpdateQuantity, dispatchQuantityUpdateAction, draftOrder?.uuid],
    );

  const updateAndClearQuantityCallback = useCallback(
    async (
      source: string,
      pendingQuantities: QuantityOverrides = {},
      options?: UpdateCartOptions,
    ) => {
      // Cancel any pending request, manually update quantity to detect failures
      debouncedUpdateQuantity.cancel();

      const result = await updateCartCallback(
        pendingQuantities,
        { orderUUID: draftOrder?.uuid, source },
        options,
      );

      // Keep local state so unsynced quantities are retried on the next update
      if (hasUnsavedQuantities(result)) {
        return result;
      }

      // Clear local state and only read from cache. This is to make sure
      // quantity changes made on the summary screen, which update the
      // cache and not this reducer, are accurately reflected if the user
      // navigates back
      dispatchQuantityReducerAction({
        type: OrderQuantityActions.CLEAR,
      });
      return result;
    },
    [
      debouncedUpdateQuantity,
      dispatchQuantityReducerAction,
      draftOrder?.uuid,
      updateCartCallback,
    ],
  );

  const updateOrderItemNotesCallback = useCallback(
    async (
      notes: string | null,
      variantPackUUID: string,
      options?: {
        onSuccess?: (order: NewOrder_OrderFieldsFragment) => void;
      },
    ) => {
      const draftOrderUUID = draftOrder?.uuid;
      if (!draftOrderUUID) {
        return;
      }

      // Cancel any pending quantity updates
      debouncedUpdateQuantity.cancel();

      const updatedItemMap = createOrderItemsInput(
        quantityStore.getState().quantityState,
      );
      const result = await updateCart({
        variables: {
          orderUUID: draftOrderUUID,
          updatedItems: [
            {
              // Include any pending quantity updates from the cancelled debounce
              ...(updatedItemMap[variantPackUUID]
                ? {
                    cart_details_at_time_of_order: {
                      quantity: updatedItemMap[variantPackUUID],
                    },
                  }
                : {}),
              item_notes: notes || '',
              variant_pack_id: variantPackUUID,
            },
          ],
        },
      });

      const updatedOrder = result?.data?.updateCart?.order;
      if (updatedOrder) {
        options?.onSuccess?.(updatedOrder);
      }
    },
    [debouncedUpdateQuantity, draftOrder, quantityStore, updateCart],
  );

  // Makes the order contain exactly `desiredQuantities` and nothing else.
  // Zeroes out saved orders_items, and ignores any unsaved local quantities.
  const replaceOrderQuantitiesCallback = useCallback(
    async (
      desiredQuantities: QuantityOverrides,
      source: string,
      options?: Pick<UpdateCartOptions, 'itemNotes'>,
    ) => {
      const zeroedOutQuantities = (
        draftOrder?.orders_items || []
      ).reduce<QuantityOverrides>((acc, oi) => {
        const variantPackUUID = oi.variants_pack.uuid;
        if (variantPackUUID) {
          acc[variantPackUUID] = 0;
        }
        return acc;
      }, {});

      return await updateAndClearQuantityCallback(
        source,
        {
          ...zeroedOutQuantities,
          ...desiredQuantities,
        },
        { ignoreLocalQuantityChanges: true, itemNotes: options?.itemNotes },
      );
    },
    [updateAndClearQuantityCallback, draftOrder?.orders_items],
  );

  const updateCartAndSetQuantityCallback = useCallback(
    async (source: string, pendingQuantities: QuantityOverrides = {}) => {
      // Cancel any pending request, manually update quantity to detect failures
      debouncedUpdateQuantity.cancel();

      const result = await updateCartCallback(pendingQuantities, {
        orderUUID: draftOrder?.uuid,
        source,
      });

      // Keep local state so unsynced quantities are retried on the next update
      if (hasUnsavedQuantities(result)) {
        return result;
      }

      if (result.status === 'saved' && result.order) {
        dispatchQuantityReducerAction({
          payload: {
            items: result.order.orders_items,
          },
          type: OrderQuantityActions.HYDRATE,
        });
      } else {
        dispatchQuantityReducerAction({
          type: OrderQuantityActions.CLEAR,
        });
      }

      return result;
    },
    [
      debouncedUpdateQuantity,
      dispatchQuantityReducerAction,
      draftOrder?.uuid,
      updateCartCallback,
    ],
  );

  const setNextFulfillmentCallback = useCallback(
    async (nextFulfillment: SelectedFulfillmentType) => {
      if (draftOrder) {
        await updateFulfillmentCallback({
          fulfillment_timeslot_end: nextFulfillment.timeslot?.endTime || null,
          fulfillment_timeslot_start:
            nextFulfillment.timeslot?.startTime || null,
          fulfillment_type: nextFulfillment.type,
          restaurant_desired_delivery_time: nextFulfillment.date,
        });
      }
      selectFulfillment(nextFulfillment);
    },
    [draftOrder, selectFulfillment, updateFulfillmentCallback],
  );

  const clearCartCallback = useCallback(async () => {
    if (isOfflineModeActive) {
      await replaceOrderQuantitiesCallback({}, 'offline_clear_cart');
      return { success: true }; // Always successful since updates are local
    } else {
      if (draftOrder && isDefaultCart(draftOrder)) {
        cancelPendingInputs();
      }

      const result = await clearCart({
        variables: {
          orderUUID: draftOrder?.uuid!,
        },
      });

      return {
        success: result.data?.clearCart.success ?? false,
      };
    }
  }, [
    cancelPendingInputs,
    clearCart,
    draftOrder,
    isOfflineModeActive,
    replaceOrderQuantitiesCallback,
  ]);

  const value = useMemo(
    () => ({
      additionalInputValueMap,
      cancelPendingInputs,
      clearCachedOrder,
      clearCart: clearCartCallback,
      createOrder,
      debouncedUpdateQuantity: debouncedUpdateQuantityCallback,
      deleteOrder,
      fulfillmentHydrated,
      nextAvailableFulfillment,
      replaceOrderQuantities: replaceOrderQuantitiesCallback,
      selectedFulfillment,
      selectFulfillment,
      setAdditionalInputValue: setAdditionalInputValueCallback,
      setNextFulfillment: setNextFulfillmentCallback,
      setOrderPaymentMethod,
      submitOrder: submitOrderCallback,
      updateAndClearQuantity: updateAndClearQuantityCallback,
      updateAndSetQuantity: updateCartAndSetQuantityCallback,
      updateFulfillment: updateFulfillmentCallback,
      updateOrderItemNotes: updateOrderItemNotesCallback,
      updateOrderMetadata,
    }),
    [
      additionalInputValueMap,
      cancelPendingInputs,
      clearCachedOrder,
      clearCartCallback,
      createOrder,
      debouncedUpdateQuantityCallback,
      deleteOrder,
      fulfillmentHydrated,
      nextAvailableFulfillment,
      replaceOrderQuantitiesCallback,
      selectFulfillment,
      selectedFulfillment,
      setAdditionalInputValueCallback,
      setNextFulfillmentCallback,
      submitOrderCallback,
      updateAndClearQuantityCallback,
      updateCartAndSetQuantityCallback,
      updateFulfillmentCallback,
      updateOrderItemNotesCallback,
      updateOrderMetadata,
    ],
  );

  const loadingValue = useMemo(
    () => ({
      isUpdatingFulfillment,
      isUpdatingQuantity: isUpdatingCart,
    }),
    [isUpdatingCart, isUpdatingFulfillment],
  );

  return (
    <NewOrderActionsContext.Provider value={value}>
      <NewOrderLoadingContext.Provider value={loadingValue}>
        {children}
      </NewOrderLoadingContext.Provider>
    </NewOrderActionsContext.Provider>
  );
};

export const DummyNewOrderActionsContextProvider: React.FC<Props> = ({
  children,
}) => {
  const value = {
    additionalInputValueMap: {},
    cancelPendingInputs: () => {},
    clearCachedOrder: async () => {},
    clearCart: async () => {
      return { success: false };
    },
    createOrder: async () => ({}) as never,
    debouncedUpdateQuantity: () => {},
    deleteOrder: async () => ({}) as never,
    fulfillmentHydrated: false,
    nextAvailableFulfillment: null,
    replaceOrderQuantities: async () => {
      return { status: 'no_changes' as const };
    },
    selectedFulfillment: null,
    selectFulfillment: () => {},
    setAdditionalInputValue: () => {},
    setNextFulfillment: async () => {},
    setOrderPaymentMethod: () => {},
    submitOrder: async () => {
      return { loyaltyEarns: [], order: null, splitOrderUUIDs: [] };
    },
    updateAndClearQuantity: async () => {
      return { status: 'no_changes' as const };
    },
    updateAndSetQuantity: async () => {
      return { status: 'no_changes' as const };
    },
    updateFulfillment: async () => {},
    updateOrderItemNotes: async () => {},
    updateOrderMetadata: async () => ({}) as never,
  };
  return (
    <NewOrderActionsContext.Provider value={value}>
      <NewOrderLoadingContext.Provider
        value={{ isUpdatingFulfillment: false, isUpdatingQuantity: false }}
      >
        {children}
      </NewOrderLoadingContext.Provider>
    </NewOrderActionsContext.Provider>
  );
};
